import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  addModelTab,
  removeModelTab,
  setActiveTabId,
  setModelDataCacheFull,
} from '../../../store/textractorSlice';
import FabricTree from '../dataFabrics/FabricTree';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import ExtractionModelForm from './ExtractionModelForm';
import ExtractionModelList from './ExtractionModelList';
import ModelDataViewer from './ModelDataViewer';
import axios from 'axios';

/**
 * Textractor core workspace — shell-agnostic.
 *
 * Rendered by:
 *  - TextractorManagement (standalone /textractor route)
 *  - ScreeningSpaceProjectView (embedded inside an SLR project)
 *
 * The shell (main-window / TopNav / SideNav / container-fluid) lives in the
 * parent. Everything functional — header actions, model list, tabs, viewer,
 * modals and popups — stays here so both mounts behave identically.
 */
const TextractorWorkspace = forwardRef(({
  fabricId = null,          // when set, Textractor is locked to this fabric
  gridThreadId = null,
  slrEntry = null,          // SLR template entry: { tableId, tableName, fabricId, fabricName, action }
  headerClassName = '',     // embedded shells pass "position-static"
  embedded = false,         // true when rendered inside an SLR project
  onFormStateChange,
  headerLeft = null,        // embedded: title + Create Model, above the model list
  headerActions = null,     
}, ref) => {
  const dispatch = useDispatch();

  // ═══ Redux-persisted state (survives page navigation) ═══
  const {
    openModelTabs: allOpenModelTabs,
    activeTabId: storedActiveTabId,
    modelDataCache,
  } = useSelector((state) => state.textractor);

  // Open tabs are global (Redux), but inside an SLR project only that project's
  // sheets are shown — sheets left open in standalone Textractor stay hidden here
  // (they are still there when the user goes back to standalone Textractor).
  const openModelTabs = (embedded && fabricId != null)
    ? allOpenModelTabs.filter(tab => String(tab.fabric_id) === String(fabricId))
    : allOpenModelTabs;
  const activeTabId = openModelTabs.some(tab => tab.id === storedActiveTabId)
    ? storedActiveTabId
    : (openModelTabs[0]?.id ?? null);

  // ═══ Local transient state (resets on mount — intentional) ═══
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  // Document selection states (received from FabricTree)
  const [selectedFabricId, setSelectedFabricId] = useState(null);
  const [selectedFabricName, setSelectedFabricName] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  const [showScrollArrows, setShowScrollArrows] = useState(false);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // Delete confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  // Ref for ExtractionModelList refresh
  const modelListRef = React.useRef();

  const API_BASE_URL = '/pceapi/textractor';

  useImperativeHandle(ref, () => ({
    createModel: () => handleCreateModel(),
    exitForm: () => {
      setIsFormVisible(false);
      setEditingModel(null);
    },
  }));

  useEffect(() => {
    onFormStateChange?.(isFormVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormVisible]);

  // ═══ Wrapper for setModelDataCache prop (keeps child components unchanged) ═══
  const handleSetModelDataCache = (updaterOrValue) => {
    if (typeof updaterOrValue === 'function') {
      const newValue = updaterOrValue(modelDataCache);
      dispatch(setModelDataCacheFull(newValue));
    } else {
      dispatch(setModelDataCacheFull(updaterOrValue));
    }
  };

  // Handler for when FabricTree selection changes
  const handleFabricSelectionChange = (fabricId_, docIds, fabricName) => {
    setSelectedFabricId(fabricId_);
    setSelectedDocIds(docIds);
    setSelectedFabricName(fabricName);
  };

  // Create model handler
  const handleCreateModel = () => {
    setEditingModel(null);
    setIsFormVisible(true);
    setSelectedFabricId(fabricId || null);

    setSelectedFabricName(null);
    setSelectedDocIds([]);
  };

  // Edit model handler
  const handleEditModel = (model, fabric_id, fabric_name) => {
    const docIds = model.documents?.map(doc => doc.doc_id) || [];

    setEditingModel(model);
    setSelectedFabricId(fabric_id);
    setSelectedFabricName(fabric_name);
    setSelectedDocIds(docIds);
    setIsFormVisible(true);
  };

  // Open model in new tab
  const handleOpenModel = (model, fabric_id, fabric_name) => {
    const existingTab = openModelTabs.find(tab => tab.id === model.id);

    if (!existingTab) {
      const newTab = {
        id: model.id,
        table_name: model.table_name,
        fabric_id: fabric_id,
        fabric_name: fabric_name
      };
      dispatch(addModelTab(newTab));
      dispatch(setActiveTabId(model.id));
    } else {
      dispatch(setActiveTabId(model.id));
    }
  };

  // Open the sheet handed over by the SLR overview (create-or-open), once per entry.
  // A new click in the overview creates a new entry object, so it opens again.
  const handledSlrEntryRef = React.useRef(null);
  useEffect(() => {
    if (!slrEntry?.tableId || handledSlrEntryRef.current === slrEntry) return;
    handledSlrEntryRef.current = slrEntry;
    setIsFormVisible(false);
    setEditingModel(null);
    handleOpenModel(
      { id: slrEntry.tableId, table_name: slrEntry.tableName },
      slrEntry.fabricId,
      slrEntry.fabricName
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slrEntry]);

  // Handle delete request
  const handleDeleteRequest = (model, fabricId_, fabricName) => {
    const modelInfo = {
      ...model,
      fabric_id: fabricId_,
      fabric_name: fabricName
    };

    setModelToDelete(modelInfo);
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    if (!modelToDelete) return;

    handleProcessingStart(`Deleting model ${modelToDelete.table_name}`);

    try {
      const response = await axios.post('/pceapi/textractor/delete-model', {
        table_id: modelToDelete.id,
        fabric_id: modelToDelete.fabric_id
      }, {
        headers: { Authorization: GetCookie('access_token') }
      });

      handleProcessingEnd();

      if (response.data.status === 'failed') {
        setPopupErrorMessage(response.data.message);
        setIsErrorPopupVisible(true);
      } else {
        setPopupSuccessMessage(response.data.message || 'Model deleted successfully');
        setIsSuccessPopupVisible(true);
        // Close tab if model is open
        handleCloseTab(modelToDelete.id);
      }
    } catch (error) {
      handleProcessingEnd();
      setPopupErrorMessage('Failed to delete model');
      setIsErrorPopupVisible(true);
    } finally {
      setModelToDelete(null);
    }
  };

  // Legacy delete handler for backward compatibility
  const handleDeleteModel = async (modelId, fabricId_, modelName) => {
    const modelInfo = {
      id: modelId,
      table_name: modelName,
      fabric_id: fabricId_
    };

    setModelToDelete(modelInfo);
    setShowDeleteConfirm(true);
  };

  // Close model tab → single Redux action handles all cleanup
  const handleCloseTab = (modelId) => {
    dispatch(removeModelTab(modelId));
  };

  const handleProcessingStart = (message) => {
    setProcessingMessage(message);
    setIsProcessingPopupVisible(true);
  };

  const handleProcessingEnd = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage('');
  };

  // Close popups
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    // Refresh the model list after closing success popup
    if (modelListRef.current && modelListRef.current.fetchModels) {
      modelListRef.current.fetchModels();
    }
  };

  // Auto-scroll to active tab when it changes
  useEffect(() => {
    if (activeTabId && openModelTabs.length > 0) {
      setTimeout(() => {
        const activeButton = document.querySelector(`.tabbtn[data-tab-id="${activeTabId}"]`);
        if (activeButton) {
          activeButton.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 100);
    }
  }, [activeTabId, openModelTabs.length]);

  // Detect if tabs overflow and need scroll arrows
  useEffect(() => {
    const checkOverflow = () => {
      const container = document.querySelector('.tabs-scroll-wrapper');
      if (container) {
        const hasOverflow = container.scrollWidth > container.clientWidth;
        setShowScrollArrows(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [openModelTabs]);

  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    if (!modelToDelete) return null;

    return (
      <div>
        <div className="mb-2">
          <strong>Sheet:</strong> {modelToDelete.table_name}
        </div>
        <div className="mb-3">
          <strong>Fabric:</strong> {modelToDelete.fabric_name}
        </div>
        <p className="text-danger mb-0">This will permanently delete the sheet and all associated data. This action cannot be undone.</p>
      </div>
    );
  };

  const renderModelTabs = () => (
    <div className="nav-tabs-container mt-0 mb-1">
      {/* Left Arrow */}
      {showScrollArrows && (
        <button
          className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
          onClick={() => {
            const currentIndex = openModelTabs.findIndex(tab => tab.id === activeTabId);
            if (currentIndex > 0) {
              dispatch(setActiveTabId(openModelTabs[currentIndex - 1].id));
            }
          }}
          disabled={openModelTabs.length <= 1}
        >
          <i className="bi bi-chevron-left"></i>
        </button>
      )}
      {/* Scrollable tabs wrapper */}
      <div className="tabs-scroll-wrapper">
        {openModelTabs.map(tab => (
          <button
            key={tab.id}
            data-tab-id={tab.id}
            className={`avatar-tooltip tabbtn tabbtn-round ${activeTabId === tab.id ? 'tabbtn-active' : 'tabbtn-inactive'}`}
            onClick={() => dispatch(setActiveTabId(tab.id))}
            data-tooltip={tab.table_name || 'None'}
          >
            <TrimContent text={tab.table_name} maxLength={15} />
            <button
              className="tabbtn-close ms-2"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseTab(tab.id);
              }}
            >
              <i className="bi bi-x"></i>
            </button>
          </button>
        ))}
      </div>

      {/* Right Arrow */}
      {showScrollArrows && (
        <button
          className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
          onClick={() => {
            const currentIndex = openModelTabs.findIndex(tab => tab.id === activeTabId);
            if (currentIndex < openModelTabs.length - 1) {
              dispatch(setActiveTabId(openModelTabs[currentIndex + 1].id));
            }
          }}
          disabled={openModelTabs.length <= 1}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      )}
    </div>
  );

    return (
    <React.Fragment>
      <div className={embedded ? 'slr-extraction-root' : undefined}>
        {!embedded && (
          <div className={`ev-card-2 dashboard-header ${headerClassName}`.trim()}>
            <div></div>
            <p className="ev-med-p-dark mb-0 ev-bold-reg">
              Textractor
            </p>
            {!isFormVisible && (
              <button
                className="ev-btn-yellow-fill-round ev-bold-reg"
                style={{ whiteSpace: 'nowrap' }}
                onClick={handleCreateModel}
              >
                <i className="bi bi-plus-circle me-1"></i> Create Sheet
              </button>
            )}
            {isFormVisible && (
              <button
                className="ev-btn-lightblue-fill-round ev-bold-reg"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => {
                  setIsFormVisible(false);
                  setEditingModel(null);
                }}
              >
                Model Navigation
              </button>
            )}
          </div>
        )}

        <div className={embedded ? 'slr-extraction-embedded' : ''}>
          {/* Sidebar */}
          <div className={embedded ? 'slr-extraction-list-col' : ''}>
          {embedded && (
            <div className="slr-extraction-row-header">{headerLeft}</div>
          )}
          {isFormVisible ? (
              <FabricTree
                onSelectionChange={handleFabricSelectionChange}
                apiBaseUrl={API_BASE_URL}
                initialFabricId={editingModel ? selectedFabricId : (fabricId || null)}
                initialSelectedDocIds={editingModel ? selectedDocIds : []}
                initialFabricName={editingModel ? selectedFabricName : null}
                filterFabricId={fabricId}
                autoExpand={fabricId}
                hideDocuments={embedded}
              />
            ) : (
              <ExtractionModelList
                ref={modelListRef}
                onOpenModel={handleOpenModel}
                onEditModel={handleEditModel}
                onRefresh={() => { }}
                openModelIds={openModelTabs.map(tab => tab.id)}
                onDeleteModel={handleDeleteModel}
                onDeleteRequest={handleDeleteRequest}
                fabricIdFromSLR={fabricId}
                embedded={embedded}
                onError={(message) => {
                  setPopupErrorMessage(message);
                  setIsErrorPopupVisible(true);
                }}
              />
            )}
          </div>

          {/* Main content */}
          <div className={embedded ? 'slr-extraction-main-col' : ''}>
            {embedded && (
              <div className="slr-extraction-row-header">
                {!isFormVisible && openModelTabs.length > 0
                  ? renderModelTabs()
                  : <div />}
                {headerActions}
              </div>
            )}
            <div className="inner-window1">
              {isFormVisible ? (
                <ExtractionModelForm
                  editingModel={editingModel}
                  selectedFabricId={selectedFabricId}
                  selectedDocIds={selectedDocIds}
                  selectedFabricName={selectedFabricName}
                  onSuccess={(message) => {
                    // Drop the cached grid data of the edited sheet so the viewer
                    // refetches it (new column order / names / added / removed columns)
                    if (editingModel?.id && modelDataCache[editingModel.id]) {
                      const { [editingModel.id]: _stale, ...rest } = modelDataCache;
                      handleSetModelDataCache(rest);
                    }
                    setPopupSuccessMessage(message);
                    setIsSuccessPopupVisible(true);
                    setIsFormVisible(false);
                    setEditingModel(null);
                  }}
                  onError={(message) => {
                    setPopupErrorMessage(message);
                    setIsErrorPopupVisible(true);
                  }}
                  onCancel={() => {
                    setIsFormVisible(false);
                    setEditingModel(null);
                  }}
                  onProcessingStart={handleProcessingStart}
                  onProcessingEnd={handleProcessingEnd}
                />
              ) : openModelTabs.length > 0 ? (
                <div>
                  {/* Tabs — in embedded mode these live in the header slot above */}
                  {!embedded && renderModelTabs()}

                  {/* Tab content */}
                  {activeTabId && (
                    <ModelDataViewer
                      modelId={activeTabId}
                      modelDataCache={modelDataCache}
                      fabricId={openModelTabs.find(tab => tab.id === activeTabId)?.fabric_id}
                      modelName={openModelTabs.find(tab => tab.id === activeTabId)?.table_name}
                      setModelDataCache={handleSetModelDataCache}
                      gridThreadIdFromSLR={gridThreadId}
                    />
                  )}
                </div>
              ) : (
                <div className=" d-flex justify-content-center align-items-center ev-card-4 dashboard-landing-container  mt-2">
                  <div className="text-center">
                    <p className="ev-xlarge-p-dark-blue mb-1"><i className="bi bi-bricks"></i></p>
                    <p className="ev-radius-large ev-fs-11 mt-1 ev-message-card2">Create or select an extraction model to begin</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setModelToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        objectName="model"
        itemName={modelToDelete ? modelToDelete.table_name : ''}
        bodyContent={getDeleteModalBodyContent()}
      />

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </React.Fragment>
  );
});

TextractorWorkspace.displayName = 'TextractorWorkspace';

export default TextractorWorkspace;